import { Stack, Alert } from '@mui/joy'
import Box from '@mui/joy/Box'
import FormControl from '@mui/joy/FormControl'
import Typography from '@mui/joy/Typography'
import { GluonEnvironment } from '@numeo/types-core'
import { useState, useEffect } from 'react'
import { isAxiosError } from 'axios'
import { useAuth } from 'hooks/auth'
import { useNavigate } from 'react-router-dom'
import Radio from '@mui/joy/Radio'
import RadioGroup from '@mui/joy/RadioGroup'
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google'
import { getStoredEnvironment } from 'setup/SetupAxios'

export function LoginPage() {
    const navigate = useNavigate()
    const { googleSignIn } = useAuth()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [environment, setEnvironment] = useState<GluonEnvironment>(GluonEnvironment.DEVELOPMENT)

    // Get environment-specific Google Client ID (reactive to environment state)
    const googleClientId =
        environment === GluonEnvironment.PRODUCTION
            ? process.env.REACT_APP_GLUON_PROD_GOOGLE_CLIENT_ID || ''
            : process.env.REACT_APP_GLUON_DEV_GOOGLE_CLIENT_ID || ''

    console.log('Google Client ID:', googleClientId)
    console.log('Environment:', environment)
    console.log('Production:', process.env.REACT_APP_GLUON_PROD_GOOGLE_CLIENT_ID)
    console.log('Development:', process.env.REACT_APP_GLUON_DEV_GOOGLE_CLIENT_ID)

    const clientIdMissing = !googleClientId

    useEffect(() => {
        // Prefer environment from token; fallback to stored env
        const env = getStoredEnvironment()
        setEnvironment(env)
    }, [])

    const handleEnvironmentChange = (env: GluonEnvironment) => {
        setEnvironment(env)
        localStorage.setItem('selected_environment', env)
    }

    const onGoogleSuccess = async (credentialResponse: CredentialResponse) => {
        if (!credentialResponse.credential) {
            setError('Failed to get Google credentials')
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            await googleSignIn(credentialResponse.credential, environment)
            navigate('/dashboard')
        } catch (err) {
            console.error('Google login error:', err)
            const errorMessage =
                (isAxiosError<{ error?: string }>(err) ? err.response?.data?.error : undefined) || 'Failed to login with Google. Please try again.'
            setError(errorMessage)
        } finally {
            setIsLoading(false)
        }
    }

    const onGoogleError = () => {
        setError('Google sign-in failed. Please try again.')
    }

    return (
        <GoogleOAuthProvider key={environment} clientId={googleClientId}>
            <Box
                component="main"
                sx={{
                    my: 'auto',
                    py: 4,
                    pb: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    width: 400,
                    maxWidth: '100%',
                    mx: 'auto',
                    borderRadius: 'sm',
                }}
            >
                {/* Header */}
                <Box sx={{ textAlign: 'center', padding: 2, borderRadius: 'sm' }}>
                    <Typography component="h1" level="h1">
                        Sign in to Gluon
                    </Typography>
                    <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
                        Secure access with Google authentication
                    </Typography>
                </Box>

                {clientIdMissing && (
                    <Alert color="warning" variant="soft">
                        Missing Google Client ID for {environment}. Please set{' '}
                        <code>
                            {environment === GluonEnvironment.PRODUCTION ? 'REACT_APP_GLUON_PROD_GOOGLE_CLIENT_ID' : 'REACT_APP_GLUON_DEV_GOOGLE_CLIENT_ID'}
                        </code>{' '}
                        and reload.
                    </Alert>
                )}

                {error && (
                    <Alert color="danger" variant="soft">
                        {error}
                    </Alert>
                )}

                {/* Environment Selection */}
                <FormControl>
                    <Stack direction={'row'} alignItems={'center'} spacing={2} sx={{ justifyContent: 'center' }}>
                        <RadioGroup
                            value={environment}
                            name="environment"
                            sx={{ flexDirection: 'row', gap: 3, justifyContent: 'center', alignItems: 'center', display: 'flex' }}
                            onChange={(e) => handleEnvironmentChange(e.target.value as GluonEnvironment)}
                        >
                            <Stack direction={'row'} alignItems={'center'} spacing={2} sx={{ justifyContent: 'center' }}>
                                <Radio value={GluonEnvironment.DEVELOPMENT} label="Staging" checked={environment === GluonEnvironment.DEVELOPMENT} />
                            </Stack>
                            <Stack direction={'row'} alignItems={'center'} spacing={2} sx={{ justifyContent: 'center' }}>
                                <Radio value={GluonEnvironment.PRODUCTION} label="Production" checked={environment === GluonEnvironment.PRODUCTION} />
                            </Stack>
                        </RadioGroup>
                    </Stack>
                </FormControl>

                {/* Google Sign-In Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <GoogleLogin onSuccess={onGoogleSuccess} onError={onGoogleError} useOneTap text="signin_with" width="400" />
                </Box>

                {isLoading && (
                    <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.tertiary' }}>
                        Signing in...
                    </Typography>
                )}
            </Box>
        </GoogleOAuthProvider>
    )
}
